import { AnimateLayoutChanges, defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { HeadlessUI, Tooltip } from '@systemeio/ui'
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props'
import React, { PropsWithChildren, useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'
import { ModuleId, QuizInterface } from '../types/module-interface'
import { ChevronDownIcon, QuizStatusIcon } from '@systemeio/ui'

export type CourseModuleProps = PropsWithChildren & {
  childrenCount: number
  openDefault: boolean
  module: {
    id: ModuleId
    name: string
    lectures: unknown[]
    quiz?: QuizInterface | null
  }
  moduleActions: React.ReactNode
  onSelect?: (CurriculumId: number) => void
}

const animateLayoutChanges: AnimateLayoutChanges = args =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true })

export function CourseModule({
  module,
  children,
  childrenCount,
  openDefault,
  moduleActions,
  onSelect,
}: CourseModuleProps) {
  const { t } = useLocoTranslation()

  // we should keep empty modules opened because it's better for UX and also closed empty modules caused this error https://app.rollbar.com/a/systemeio/fix/item/dashboard-frontend/8260
  const shouldBeAlwaysOpened = module.lectures.length === 0

  const [open, setOpen] = useState(shouldBeAlwaysOpened)

  useEffect(() => {
    if (shouldBeAlwaysOpened) setOpen(shouldBeAlwaysOpened)
  }, [shouldBeAlwaysOpened])

  const { attributes, isDragging, listeners, setNodeRef, transition, transform } = useSortable({
    id: module.id,
    data: {
      type: 'container',
      children: module.lectures,
    },
    animateLayoutChanges,
  })
  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : undefined,
  }

  return (
    <div
      className="flex cursor-move flex-col gap-3 focus-visible:outline-hidden"
      ref={setNodeRef}
      style={{ ...style }}
      {...attributes}
      {...listeners}
      data-test={`course-module-${module.id}-div`}
    >
      <div className="z-0 overflow-hidden rounded-lg">
        <HeadlessUI.Disclosure defaultOpen={openDefault || shouldBeAlwaysOpened}>
          <>
            <div
              className={`bg-brand-primary-subtle flex items-center ${
                childrenCount > 0 ? '' : 'overflow-hidden rounded-b-lg'
              }`}
            >
              <div
                onClick={() => onSelect?.(module.id)}
                className="order-1 flex-[25_1_0%] truncate lg:flex-[55_1_0%]"
              >
                <HeadlessUI.DisclosureButton
                  onClick={() => setOpen(shouldBeAlwaysOpened || !open)}
                  className={
                    'text-text-primary font-inter text-start relative flex h-full w-full max-w-fit cursor-pointer items-center gap-2 px-4 py-4 text-base font-bold lg:px-6 lg:text-[17px]'
                  }
                  {...nonDraggableProps}
                  data-test={'course-module-button-show-lecture'}
                >
                  <ChevronDownIcon
                    className={`h-3 w-3 ${open ? 'rotate-180 transform' : ''} shrink-0`}
                    aria-hidden="true"
                  />
                  <span className="truncate" data-test={'course-module-name'}>
                    {module.name}
                  </span>
                </HeadlessUI.DisclosureButton>
              </div>
              <div className="order-2 hidden flex-[15_1_0%] px-6 py-4 lg:block">
                <div />
              </div>
              <div
                className={
                  'order-2 flex flex-[5_1_0%] items-center justify-end gap-6 px-4 py-4 lg:order-3 lg:px-6'
                }
              >
                {module.quiz && (
                  <Tooltip
                    label={
                      module.quiz.published
                        ? t('dashboard.course.module_quiz_active')
                        : t('dashboard.course.module_quiz_inactive')
                    }
                    mode="hover"
                  >
                    <QuizStatusIcon
                      className={twMerge(
                        `h-[20px] w-[20px]`,
                        module.quiz.published ? 'fill-brand-primary' : 'fill-fill-primary',
                      )}
                    />
                  </Tooltip>
                )}
                {moduleActions}
              </div>
            </div>
            <HeadlessUI.Transition
              show={open}
              enter="transition ease duration-500 transform"
              enterFrom="opacity-0 translate-y-0"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease duration-300 transform"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-0"
              data-test={'course-module-expanded-attribute'}
            >
              <HeadlessUI.DisclosurePanel static className="bg-background-secondary">
                {children}
              </HeadlessUI.DisclosurePanel>
            </HeadlessUI.Transition>
          </>
        </HeadlessUI.Disclosure>
      </div>
    </div>
  )
}
